import { useEffect, useRef, useState } from 'react';

const TOP_NAV_HEIGHT = 64;
const SIDEBAR_TOP = TOP_NAV_HEIGHT + 16;

interface UsePinnedSidebarOptions {
    scrollContainerRef?: React.RefObject<HTMLElement | null>;
    scrollableSidebar?: boolean;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    deps?: React.DependencyList;
}

interface UsePinnedSidebarResult {
    sidebarContainerRef: React.RefObject<HTMLDivElement | null>;
    sidebarContentRef: React.RefObject<HTMLDivElement | null>;
    containerStyle: React.CSSProperties | undefined;
    contentClassName: string;
    contentStyle: React.CSSProperties | undefined;
}

export function usePinnedSidebar(options: UsePinnedSidebarOptions = {}): UsePinnedSidebarResult {
    const { scrollContainerRef, scrollableSidebar = true, deps = [] } = options;

    const [isSidebarPinned, setIsSidebarPinned] = useState(false);
    const [sidebarRect, setSidebarRect] = useState<{ left: number; width: number; height: number }>({
        left: 0,
        width: 0,
        height: 0,
    });
    const sidebarContainerRef = useRef<HTMLDivElement | null>(null);
    const sidebarContentRef = useRef<HTMLDivElement | null>(null);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        const scrollTarget: EventTarget =
            scrollContainerRef?.current ??
            document.getElementById('main-scroll-area') ??
            window;

        const updateSidebarPosition = () => {
            const container = sidebarContainerRef.current;
            const content = sidebarContentRef.current;
            if (!container || !content) return;

            if (window.innerWidth < 768) {
                setIsSidebarPinned(false);
                return;
            }

            const containerRect = container.getBoundingClientRect();
            const nextRect = {
                left: containerRect.left,
                width: containerRect.width,
                height: content.offsetHeight,
            };

            setSidebarRect(prev =>
                prev.left === nextRect.left && prev.width === nextRect.width && prev.height === nextRect.height
                    ? prev
                    : nextRect
            );

            setIsSidebarPinned(containerRect.top <= SIDEBAR_TOP);
        };

        updateSidebarPosition();
        scrollTarget.addEventListener('scroll', updateSidebarPosition, { passive: true } as AddEventListenerOptions);
        window.addEventListener('resize', updateSidebarPosition);

        return () => {
            scrollTarget.removeEventListener('scroll', updateSidebarPosition);
            window.removeEventListener('resize', updateSidebarPosition);
        };
    }, deps);

    const containerStyle: React.CSSProperties | undefined = isSidebarPinned
        ? { height: sidebarRect.height }
        : undefined;

    const contentClassName = isSidebarPinned ? 'fixed z-40' : '';

    const contentStyle: React.CSSProperties | undefined = isSidebarPinned
        ? {
            top: SIDEBAR_TOP,
            left: sidebarRect.left,
            width: sidebarRect.width,
            ...(scrollableSidebar && {
                maxHeight: `calc(100vh - ${SIDEBAR_TOP}px)`,
                overflowY: 'auto' as const,
                overscrollBehavior: 'contain',
            }),
        }
        : undefined;

    return {
        sidebarContainerRef,
        sidebarContentRef,
        containerStyle,
        contentClassName,
        contentStyle,
    };
}
